import React, { useState } from "react";
import { Save, KeyRound, User, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, useAuth, formatApiError } from "@/contexts/AuthContext";

const Field = ({ label, hint, children }) => (
  <div><Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">{label}</Label><div className="mt-1">{children}</div>{hint && <div className="text-[10px] text-[#6C757D] mt-1">{hint}</div>}</div>
);

export default function MyProfile() {
  const { user, refreshMe } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const roleLabel = user?.role === "admin" ? "Super Admin" : user?.role === "agency_admin" ? "Agency Admin" : user?.role === "support" ? "Support / Production" : "Agent";

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Enter your name"); return; }
    setSaving(true);
    try { await api.put("/auth/profile", { name, phone }); await refreshMe(); toast.success("Profile updated"); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Update failed"); } finally { setSaving(false); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.next !== pw.confirm) { toast.error("Passwords don't match"); return; }
    setPwSaving(true);
    try { await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next }); toast.success("Password changed"); setPw({ current: "", next: "", confirm: "" }); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Could not change password"); } finally { setPwSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="my-profile-page">
        <div className="mb-8 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#0B132B] text-[#D4AF37] font-black text-xl grid place-items-center" data-testid="profile-avatar">{(user?.name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("")}</div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">{roleLabel}{user?.agency ? ` · ${user.agency}` : ""}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#0B132B] tracking-tight leading-none">My Profile</h1>
          </div>
        </div>

        <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6">
          <form onSubmit={save} className="space-y-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]"><User size={13} className="text-[#D4AF37]" /> Personal information</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name"><Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 rounded-sm" data-testid="profile-name" /></Field>
              <Field label="Phone" hint="Shown on proformas, rate cards and email footers."><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="h-10 rounded-sm" data-testid="profile-phone" /></Field>
              <Field label="Email" hint="Your sign-in email. Contact Advantage to change it."><Input value={user?.email || ""} disabled className="h-10 rounded-sm bg-[#F8F9FA]" data-testid="profile-email" /></Field>
              <Field label="Agency" hint="Set by your agency admin."><Input value={user?.agency || "—"} disabled className="h-10 rounded-sm bg-[#F8F9FA]" data-testid="profile-agency" /></Field>
            </div>
            <Button type="submit" disabled={saving} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="profile-save">{saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />} Save changes</Button>
          </form>
        </Card>

        <Card className="p-6 border-[#E9ECEF] rounded-sm" data-testid="profile-password-card">
          <form onSubmit={changePw} className="space-y-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]"><KeyRound size={13} className="text-[#D4AF37]" /> Change password</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Current password"><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" className="h-10 rounded-sm" data-testid="profile-pw-current" /></Field>
              <Field label="New password" hint="At least 8 characters."><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} autoComplete="new-password" className="h-10 rounded-sm" data-testid="profile-pw-new" /></Field>
              <Field label="Confirm new password"><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} autoComplete="new-password" className="h-10 rounded-sm" data-testid="profile-pw-confirm" /></Field>
            </div>
            <Button type="submit" variant="outline" disabled={pwSaving || !pw.current || !pw.next} className="h-10 rounded-sm" data-testid="profile-pw-save">{pwSaving ? <Loader2 size={14} className="animate-spin mr-2" /> : <KeyRound size={14} className="mr-2" />} Update password</Button>
          </form>
        </Card>
      </main>
    </div>
  );
}
